import { CSSProperties, useState } from 'react';
import { stableColorFor } from '../content/imageGradients';

/**
 * ConversationRow — one entry in a chat inbox list: identity-tinted initials
 * avatar (optional photo, optional online dot), name + relative time, an
 * optional trip line with a status dot, and the last-message preview with a
 * gold unread-count badge.
 *
 * Selected rows fill with --nav-active-bg and carry the inset gold keyline.
 */
export interface ConversationRowProps {
  /** Traveler / guest display name. */
  name: string;
  /** 1–2 letter initials for the avatar disc. */
  initials: string;
  /** Seed for the stable avatar colour (e.g. traveler id). Default: name. */
  colorSeed?: string;
  /** Photo URL — replaces the initials disc. */
  avatarUrl?: string | null;
  /** Green presence dot on the avatar. */
  online?: boolean;
  /** Relative time label, e.g. "2h". */
  time?: string;
  /** Trip line under the name, e.g. "Solo Signature Lisbon". */
  trip?: string;
  /** CSS colour for the trip status dot. */
  statusColor?: string;
  /** Last-message preview (prefix "You: " upstream for own messages). */
  preview?: string;
  /** Unread count badge; also emboldens the preview. 0/undefined hides it. */
  unreadCount?: number;
  /** Highlight as the open conversation. */
  selected?: boolean;
  /** 'comfortable' (default, with trip line) or 'compact'. */
  density?: 'comfortable' | 'compact';
  onClick?: () => void;
  style?: CSSProperties;
}

export function ConversationRow({
  name, initials, colorSeed, avatarUrl, online = false,
  time, trip, statusColor, preview, unreadCount = 0,
  selected = false, density = 'comfortable', onClick, style,
}: ConversationRowProps) {
  const [hover, setHover] = useState(false);
  const compact = density === 'compact';
  const unread = unreadCount > 0;
  const avatarSize = compact ? 38 : 44;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', gap: 11, alignItems: 'center',
        padding: compact ? '9px 14px' : '11px 14px', borderRadius: 10,
        cursor: 'pointer', transition: 'background .14s ease',
        fontFamily: 'var(--font-sans)',
        background: selected ? 'var(--nav-active-bg)' : hover ? 'var(--surface)' : 'transparent',
        boxShadow: selected ? 'inset 3px 0 0 rgb(var(--gold))' : 'none',
        ...style,
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: avatarSize, height: avatarSize, borderRadius: '50%',
          background: avatarUrl ? undefined : stableColorFor(colorSeed ?? name),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, position: 'relative',
        }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-subhead)', color: 'rgb(var(--cream))', lineHeight: 1, letterSpacing: '.02em' }}>{initials}</span>
        )}
        {online ? (
          <span style={{ position: 'absolute', bottom: -1, right: -1, width: 11, height: 11, borderRadius: '50%', background: 'rgb(var(--success))', border: '2px solid var(--bg)' }} />
        ) : null}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 'var(--fs-footnote)', fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          {time ? <span style={{ fontSize: 'var(--fs-caption2)', color: 'var(--text-dim)', flexShrink: 0 }}>{time}</span> : null}
        </div>
        {trip && !compact ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: statusColor ?? 'var(--text-dim)', flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trip}</span>
          </div>
        ) : null}
        {preview != null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-caption)', color: unread ? 'var(--text)' : 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{preview}</span>
            {unread ? (
              <span style={{ background: 'rgb(var(--gold))', color: 'rgb(var(--ink))', fontSize: 'var(--fs-caption2)', fontWeight: 700, borderRadius: 999, padding: '1px 6px', minWidth: 18, textAlign: 'center', flexShrink: 0 }}>{unreadCount}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ConversationRow;
